import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ThumbsUp } from "lucide-react";

const benefits = [
  "Bad credit, low credit, or no credit — all credit situations are welcome",
  "New to Canada? We help customers with little or no Canadian credit history",
  "If you have a steady income or full-time job, we’ll work hard to help you get approved",
  "We work with multiple lenders to find financing options that fit your situation",
  "Our goal is to help you get into a reliable vehicle and move forward with confidence",
];

const CONTACT_API_URL = "https://bbadmin.squaretrigger.com/api/contact";

const INQUIRY_TYPE_LABELS = {
  general: "General Inquiry",
  finance: "Financing",
  parts: "Parts & Accessories",
  service: "Service & Maintenance",
} as const;

const PREFERRED_CONTACT_LABELS = {
  call: "Phone Call",
  email: "Email",
  text: "Text Message",
} as const;

const BEST_TIME_LABELS = {
  anytime: "Anytime",
  morning: "Morning (9AM - 12PM)",
  afternoon: "Afternoon (12PM - 5PM)",
  evening: "Evening (5PM - 8PM)",
} as const;

export default function FinancingSection() {
  const [formData, setFormData] = useState({
    name: "",
    inquiryType: "general",
    email: "",
    phone: "",
    preferredContact: "call",
    bestTime: "anytime",
    message: "",
    offers: false,
    terms: false,
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setRequestNo] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9()+\-\s]{10,20}$/;

  const inquiryOptions = [
    { value: "general", label: INQUIRY_TYPE_LABELS.general },
    { value: "finance", label: INQUIRY_TYPE_LABELS.finance },
    { value: "parts", label: INQUIRY_TYPE_LABELS.parts },
    { value: "service", label: INQUIRY_TYPE_LABELS.service },
  ];

  const contactOptions = [
    { value: "call", label: PREFERRED_CONTACT_LABELS.call },
    { value: "email", label: PREFERRED_CONTACT_LABELS.email },
    { value: "text", label: PREFERRED_CONTACT_LABELS.text },
  ];

  const bestTimeOptions = [
    { value: "anytime", label: BEST_TIME_LABELS.anytime },
    { value: "morning", label: BEST_TIME_LABELS.morning },
    { value: "afternoon", label: BEST_TIME_LABELS.afternoon },
    { value: "evening", label: BEST_TIME_LABELS.evening },
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "First and last name is required";
    }

    if (!formData.inquiryType.trim()) {
      newErrors.inquiryType = "Please select an inquiry type";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (formData.phone.trim() && !phoneRegex.test(formData.phone)) {
      newErrors.phone = "Enter a valid phone number";
    }

    if (!formData.preferredContact.trim()) {
      newErrors.preferredContact = "Please select a contact method";
    }

    if (!formData.bestTime.trim()) {
      newErrors.bestTime = "Please select the best time to call";
    }

    if (!formData.terms) {
      newErrors.terms = "You must agree before sending your message";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearFieldError = (fieldName: string) => {
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
    if (submitError) {
      setSubmitError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const payload = {
        name: formData.name.trim(),
        inquiryType:
          INQUIRY_TYPE_LABELS[
            formData.inquiryType as keyof typeof INQUIRY_TYPE_LABELS
          ] ?? formData.inquiryType,
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        preferredContact:
          PREFERRED_CONTACT_LABELS[
            formData.preferredContact as keyof typeof PREFERRED_CONTACT_LABELS
          ] ?? formData.preferredContact,
        bestTime:
          BEST_TIME_LABELS[
            formData.bestTime as keyof typeof BEST_TIME_LABELS
          ] ?? formData.bestTime,
        message: formData.message.trim(),
        offers: formData.offers ? "Y" : "N",
        terms: formData.terms ? "Y" : "N",
      };

      const response = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(
          result?.message || "Unable to submit your details right now.",
        );
      }

      setRequestNo(result?.data?.request_no ?? "");
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Something went wrong while sending your details.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputBaseClass =
    "w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-transparent transition-all bg-white";

  const getFieldClass = (fieldName: string) =>
    `${inputBaseClass} ${errors[fieldName] ? "border-red-500" : "border-gray-200"}`;

  return (
    <section id="financing" className="py-14 bg-white w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl md:text-4xl font-bold text-gray-900 mb-2">
              SMART FINANCING
            </h2>
            <h3 className="font-serif text-2xl md:text-3xl font-semibold text-brand-red mb-6">
              FOR EVERY DREAM
            </h3>

            <div className="w-20 h-1 bg-gradient-to-r from-brand-red to-brand-gold rounded-full mb-8" />

            <ul className="space-y-4 mb-8">
              {benefits.map((benefit, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700">{benefit}</span>
                </motion.li>
              ))}
            </ul>

            <div className="relative rounded-2xl overflow-hidden h-64 hidden lg:block">
              <img
                src="/images/financing-bg.jpg"
                alt="Financing"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-brand-red/80 to-transparent flex items-center">
                <div className="p-8 text-white">
                  <p className="text-3xl font-serif font-bold mb-2">95%</p>
                  <p className="text-lg">Approval Rate</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Form / Flip Success Card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative" style={{ perspective: "2000px" }}>
              <motion.div
                animate={{ rotateY: isSubmitted ? 180 : 0 }}
                transition={{
                  duration: 0.9,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="relative"
                style={{
                  transformStyle: "preserve-3d",
                }}
              >
                {/* Front - Existing Form */}
                <div
                  className={`bg-white rounded-2xl shadow-card-hover border border-gray-100 overflow-hidden ${
                    isSubmitted ? "pointer-events-none" : ""
                  }`}
                  style={{
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                  }}
                >
                  <div className="bg-gradient-to-r from-brand-red to-red-700 p-6 text-white">
                    <h4 className="font-serif text-2xl font-bold mb-2">
                      Send Us a Message
                    </h4>
                    <p className="text-white/90 text-base">
                      Fill out the form below and we'll get back to you within
                      24 hours.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First and Last Name{" "}
                        <span className="text-brand-red">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData({ ...formData, name: e.target.value });
                          clearFieldError("name");
                        }}
                        className={getFieldClass("name")}
                        placeholder="John Smith"
                        required
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        What are you inquiring about?{" "}
                        <span className="text-brand-red">*</span>
                      </label>
                      <select
                        name="inquiryType"
                        value={formData.inquiryType}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            inquiryType: e.target.value,
                          });
                          clearFieldError("inquiryType");
                        }}
                        className={getFieldClass("inquiryType")}
                        required
                      >
                        {inquiryOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {errors.inquiryType && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.inquiryType}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address{" "}
                          <span className="text-brand-red">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={(e) => {
                            setFormData({ ...formData, email: e.target.value });
                            clearFieldError("email");
                          }}
                          className={getFieldClass("email")}
                          placeholder="john@example.com"
                          required
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.email}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={(e) => {
                            setFormData({ ...formData, phone: e.target.value });
                            clearFieldError("phone");
                          }}
                          className={getFieldClass("phone")}
                          placeholder="(604) 123-4567"
                        />
                        {errors.phone && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.phone}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Preferred Contact Method
                        </label>
                        <select
                          name="preferredContact"
                          value={formData.preferredContact}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              preferredContact: e.target.value,
                            });
                            clearFieldError("preferredContact");
                          }}
                          className={getFieldClass("preferredContact")}
                        >
                          {contactOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.preferredContact && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.preferredContact}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Best Time to Call?
                        </label>
                        <select
                          name="bestTime"
                          value={formData.bestTime}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              bestTime: e.target.value,
                            });
                            clearFieldError("bestTime");
                          }}
                          className={getFieldClass("bestTime")}
                        >
                          {bestTimeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.bestTime && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.bestTime}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dealer Message
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={(e) => {
                          setFormData({ ...formData, message: e.target.value });
                          if (submitError) setSubmitError("");
                        }}
                        rows={4}
                        className={`${inputBaseClass} border-gray-200 resize-none`}
                        placeholder="Tell us how we can help you..."
                      />
                    </div>

                    <div className="space-y-4 pt-2">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.offers}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              offers: e.target.checked,
                            });
                            if (submitError) setSubmitError("");
                          }}
                          className="mt-1 h-5 w-5 rounded border-gray-300 text-brand-red focus:ring-brand-red"
                        />
                        <span className="text-sm text-gray-700 leading-relaxed">
                          Yes, send me exclusive offers and promotions from Big
                          Boys Motor Club and its partners. I can withdraw my
                          consent at any time.
                        </span>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.terms}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              terms: e.target.checked,
                            });
                            clearFieldError("terms");
                          }}
                          className="mt-1 h-5 w-5 rounded border-gray-300 text-brand-red focus:ring-brand-red"
                        />
                        <span className="text-sm text-gray-700 leading-relaxed">
                          By clicking "Send" I agree to receive communications
                          and share my personal information with Big Boys Motor
                          Club and its partners. I also agree to the{" "}
                          <a
                            href="/terms-and-conditions"
                            className="text-brand-red hover:underline"
                          >
                            Terms and Conditions
                          </a>{" "}
                          and the{" "}
                          <a
                            href="/privacy-policy"
                            className="text-brand-red hover:underline"
                          >
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>

                      {errors.terms && (
                        <p className="-mt-2 text-sm text-red-500">
                          {errors.terms}
                        </p>
                      )}
                    </div>

                    <motion.button
                      whileHover={
                        formData.terms && !isSubmitting ? { scale: 1.02 } : {}
                      }
                      whileTap={
                        formData.terms && !isSubmitting ? { scale: 0.98 } : {}
                      }
                      type="submit"
                      disabled={!formData.terms || isSubmitting}
                      className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors ${
                        formData.terms && !isSubmitting
                          ? "bg-brand-red text-white hover:bg-red-700 shadow-glow-red"
                          : "bg-red-300 text-white cursor-not-allowed"
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <ThumbsUp className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </motion.button>

                    {submitError && (
                      <p className="text-center text-sm text-red-500">
                        {submitError}
                      </p>
                    )}

                    <p className="text-center text-xs text-gray-500">
                      We’ll review your details and contact you as soon as
                      possible.
                    </p>
                  </form>
                </div>

                {/* Back - Success Flip Card */}
                <div
                  className="absolute inset-0 bg-white rounded-2xl shadow-card-hover border border-gray-100 overflow-hidden flex flex-col"
                  style={{
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                  }}
                >
                  <div className="bg-gradient-to-r from-brand-red to-red-700 p-6 text-white">
                    <h4 className="font-serif text-2xl font-bold mb-2">
                      Request Submitted
                    </h4>
                    <p className="text-white/90 text-base">
                      Thank you for sharing your details with us.
                    </p>
                  </div>

                  <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 text-center bg-[radial-gradient(circle_at_top,rgba(220,38,38,0.08),transparent_42%)]">
                    <h5 className="font-serif text-3xl font-bold text-gray-900 mb-3">
                      Details Submitted Successfully
                    </h5>

                    <p className="max-w-md text-gray-600 text-base sm:text-lg leading-relaxed mb-7">
                      We’ve received your details successfully and our team will
                      be in touch within a few hours.
                    </p>

                    <div className="mb-6 flex items-center justify-center">
                      <span
                        className="text-9xl text-green-600 leading-none"
                        role="img"
                        aria-label="smiling face"
                      >
                        ☺
                      </span>
                    </div>

                    {/* {requestNo && (
                      <span className="mb-5 inline-flex items-center rounded-full bg-green-50 border border-green-100 px-4 py-2 text-sm font-semibold text-green-700">
                        Request No: {requestNo}
                      </span>
                    )} */}

                    <div className="mt-6 rounded-2xl bg-green-50 border border-green-100 px-5 py-4">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        Thank you for choosing Big Boys Motor Club. We’ll review
                        your request carefully and contact you as soon as
                        possible.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
